/* The one analytics value-type vocabulary, shared by every edge that writes or renders a type: the
   columns editor and reconciler in cirra, the stat tile's value, and the analytics_panel_update tool
   in the api. It lives here because a writer with a narrower private list silently destroys the
   types it does not know — that is how html columns broke once, and how the tool (which only ever
   sends "html") would erase the date/number/list types the reconciler infers. Add a type here and
   every edge keeps it.

   Naming rule so keys stay coherent across panel types: wherever a displayed thing is an entry
   object the field is `type` (table columns `{ key, type }`, chart series `{ key, show, type }`);
   wherever the pointer is a flat `<x>Key` the type is its sibling `<x>Type` (the stat tile's
   `valueKey` / `valueType`). Every one of those fields carries a value from THIS list and is read
   through normalizeAnalyticsColumnType.

   Labels ship with the values so a type can never exist without a human name for it. */
export const ANALYTICS_COLUMN_TYPE_OPTIONS = [
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'percent', label: 'Percent' },
  { value: 'currency', label: 'Currency' },
  { value: 'duration', label: 'Duration' },
  { value: 'rank', label: 'Rank' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'date', label: 'Date' },
  { value: 'object', label: 'Object' },
  { value: 'list', label: 'List' },
  { value: 'html', label: 'HTML' }
];

export const ANALYTICS_COLUMN_TYPES = ANALYTICS_COLUMN_TYPE_OPTIONS.map((option) => option.value);

/* Names other systems use for the same type — app response schemas and DuckDB both speak these. */
const TYPE_ALIASES = {
  integer: 'number',
  float: 'number',
  decimal: 'number',
  double: 'number',
  bigint: 'number',
  datetime: 'date',
  timestamp: 'date',
  array: 'list',
  bool: 'boolean',
  text: 'string',
  varchar: 'string',
  /* Presentation synonyms the model and app schemas both reach for. */
  pct: 'percent',
  percentage: 'percent',
  money: 'currency',
  position: 'rank',
  seconds: 'duration'
};

export const normalizeAnalyticsColumnType = (type) => {
  const normalized = String(type || '')
    .trim()
    .toLowerCase();

  return TYPE_ALIASES[normalized] || normalized;
};

/* WHAT AN ABSENT VALUE MEANS, declared per type instead of guessed at the render site.

   An empty result is not an error — a window with no rows makes SUM and AVG return null — but the
   honest rendering of that null depends on the measure:

     count/sum        → 0. "Total clicks: 0" is true.
     percent/rank/avg → no value. 0 would LIE: "average position 0" reads better than rank 1, and
                        "CTR 0%" claims impressions that were never served.

   `number` and `currency` default to zero because a KPI reading a plain number is overwhelmingly a
   count or a sum; anything whose null is genuinely undefined declares its own type (percent, rank)
   or overrides with `emptyAs`. Nothing here depends on the transform author remembering to guard:
   the display layer decides, so a panel cannot render "null%" however sloppy the code upstream. */
const TYPE_EMPTY_BEHAVIOUR = {
  number: 'zero',
  currency: 'zero',
  percent: 'none',
  rank: 'none',
  duration: 'none',
  boolean: 'none',
  date: 'none',
  string: 'none',
  object: 'none',
  list: 'none',
  html: 'none'
};

export const EMPTY_VALUE_TEXT = '—';

export const resolveAnalyticsEmptyBehaviour = ({ type, emptyAs = '' } = {}) => {
  const override = String(emptyAs || '')
    .trim()
    .toLowerCase();

  if (override === 'zero' || override === 'none') {
    return override;
  }

  return TYPE_EMPTY_BEHAVIOUR[normalizeAnalyticsColumnType(type)] || 'none';
};

/* Is this value actually of its declared type? The guard that keeps a badly-written transform from
   putting garbage on a panel: a percent handed the string "null%" is not a number, so the panel
   renders its empty state instead of the garbage, and the caller can report the mismatch. */
const toFiniteNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }

  return null;
};

export const ANALYTICS_NUMERIC_COLUMN_TYPES = ['number', 'percent', 'currency', 'duration', 'rank'];
const NUMERIC_TYPES = ANALYTICS_NUMERIC_COLUMN_TYPES;

const groupDigits = (value, maximumFractionDigits) => new Intl.NumberFormat('en-US', { maximumFractionDigits }).format(value);

/* How many items of a list are printed before the rest are counted. A list column exists to say WHO
   or WHAT is behind a number — the five names under a bar, not all 88,403 of them — so the reader
   gets a sample and the size of what they are not seeing. Capped here rather than at each render
   site so a table cell and a chart's hover card cut the same list at the same place.

   The cap is a display bound, not a data bound: the SQL that builds the column should still cut it
   (`list(owner_name)[1:5]`), because a row carrying thousands of names pays for them in every
   payload whether or not anything prints them. */
const LIST_PREVIEW_LIMIT = 5;

const formatDuration = (totalSeconds) => {
  const seconds = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  }

  if (minutes > 0) {
    return `${minutes}m ${String(seconds % 60).padStart(2, '0')}s`;
  }

  return `${seconds}s`;
};

/* THE formatter. One implementation for every surface that shows a typed value — stat tiles, table
   cells, and (next) chart axes and tooltips — so the same number reads identically wherever it
   appears, and an absent value follows its type's declared meaning rather than each renderer's
   local guess. Returns display text, always a string. */
export const formatAnalyticsTypedValue = ({ value, type = '', emptyAs = '', currency = 'USD' } = {}) => {
  const normalizedType = normalizeAnalyticsColumnType(type);
  /* A list with nothing in it is absent, not a list: an empty aggregate is the same fact as a null
     one, and printing "" would read as a value that happens to be blank. */
  const isAbsent = value === null || value === undefined || value === '' || (Array.isArray(value) && value.length < 1);
  const numeric = NUMERIC_TYPES.includes(normalizedType) ? toFiniteNumber(value) : null;
  /* A numeric type holding something non-numeric is treated as absent: that is the "null%" guard. */
  const isEmpty = isAbsent || (NUMERIC_TYPES.includes(normalizedType) && numeric === null);

  if (isEmpty) {
    return resolveAnalyticsEmptyBehaviour({ type: normalizedType, emptyAs }) === 'zero' ? formatAnalyticsTypedValue({ value: 0, type: normalizedType, currency }) : EMPTY_VALUE_TEXT;
  }

  if (normalizedType === 'percent') {
    return `${groupDigits(numeric, 2)}%`;
  }

  if (normalizedType === 'currency') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: Number.isInteger(numeric) ? 0 : 2 }).format(numeric);
  }

  if (normalizedType === 'duration') {
    return formatDuration(numeric);
  }

  if (normalizedType === 'rank') {
    return groupDigits(numeric, 1);
  }

  if (normalizedType === 'number') {
    return groupDigits(numeric, 2);
  }

  if (normalizedType === 'boolean') {
    return value === true ? 'Yes' : 'No';
  }

  /* A list prints a sample of its items and how many it is not printing. Chosen by the value being
     an array as well as by the declared type, because no other branch here can render one and the
     untyped passthrough below would print plausible-but-wrong text for it ("a,b", unspaced and
     uncapped). Items are formatted untyped: an array carries one type for itself, not one per item,
     so a number inside one still gets its digit grouping and nothing else is claimed about it. */
  if (normalizedType === 'list' || Array.isArray(value)) {
    const items = Array.isArray(value) ? value : [value];
    const shown = items.slice(0, LIST_PREVIEW_LIMIT).map((item) => formatAnalyticsTypedValue({ value: item, currency }));
    const remaining = items.length - shown.length;

    return remaining > 0 ? `${shown.join(', ')} +${groupDigits(remaining, 0)} more` : shown.join(', ');
  }

  /* Untyped and text-ish values pass through, and a bare number still gets digit grouping so an
     un-declared count does not regress to 81453. */
  const inferred = toFiniteNumber(value);

  return inferred !== null && typeof value !== 'boolean' ? groupDigits(inferred, 2) : String(value);
};
